import useFormatter from "@/hooks/use-formatter.hook.ts";
import { OfferPreviewCustomField } from "@/pages/recruit/offers/components/OfferPreviewCustomField.tsx";
import { OfferPreviewSystemField } from "@/pages/recruit/offers/components/OfferPreviewSystemField.tsx";
import { OfferSystemFieldDisplayValues } from "@/pages/recruit/offers/components/use-offer-system-field-value.ts";
import { PublicLinkSection } from "@/pages/recruit/offers/PublicLinkSection.tsx";
import {
  getOfferPreviewFields,
  getVisibleOfferValues,
} from "@/pages/recruit/offers/offer-edit-fields.ts";
import { useEmployeesByIds } from "@/queries/employees/use-employees.query.ts";
import { useLegalEntities } from "@/queries/use-legal-entities.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { useOfferEditContext } from "@/types/recruit/offer.types.ts";
import { Avatar, Box, HtmlViewer, Stack, Text, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export function OfferEditStepPreview() {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { applicant, offerRequest, formHook, formFields } = useOfferEditContext();
  const detailsData = getVisibleOfferValues(formHook.getValues(), formFields);
  const previewFields = getOfferPreviewFields(formFields, detailsData);
  const { data: locations } = useLocations(
    previewFields.some((field) => field.type === ApiFieldTypes.offer_location)
  );
  const { data: legalEntities } = useLegalEntities(
    previewFields.some((field) => field.type === ApiFieldTypes.offer_legal_entity)
  );
  const { data: managers } = useEmployeesByIds(
    detailsData.manager_id ? [detailsData.manager_id] : []
  );
  const savedOffer = offerRequest?.offer_data;
  const manager =
    managers?.find((employee) => employee.id === detailsData.manager_id) ??
    (savedOffer?.manager?.id === detailsData.manager_id ? savedOffer?.manager : undefined);
  const legalEntity =
    legalEntities?.find((entity) => entity.id === detailsData.legal_entity_id) ??
    (savedOffer?.legal_entity?.id === detailsData.legal_entity_id
      ? savedOffer?.legal_entity
      : undefined);
  const location =
    locations?.find((item) => item.id === detailsData.location_id) ??
    (savedOffer?.location?.id === detailsData.location_id ? savedOffer?.location : undefined);

  const systemValues: OfferSystemFieldDisplayValues = {
    date_start: detailsData.date_start,
    legal_entity: legalEntity?.title ?? null,
    location: location?.title ?? null,
    manager: manager ? renderEmployeeName(manager) : null,
    salary: detailsData.salary,
  };

  return (
    <Stack gap={"lg"}>
      <Stack direction={"horizontal"} gap={"md"} items={"center"}>
        <Avatar
          url={applicant?.photo_url || offerRequest?.offer_data?.applicant?.photo_url}
          acronym={`${detailsData?.first_name} ${detailsData?.last_name}`}
          size={"xl"}
        />
        <Stack>
          <Title header={`${detailsData?.first_name} ${detailsData?.last_name}`} size={"lg"} />
          <Text className={"text-secondary"}>{detailsData.job_title}</Text>
        </Stack>
      </Stack>
      {previewFields.length > 0 && (
        <Box>
          <Title size="xs" header={t("ats:offer_details")} />
          {previewFields.map((field) =>
            field.type === ApiFieldTypes.custom ? (
              <OfferPreviewCustomField key={field.id} field={field} />
            ) : (
              <OfferPreviewSystemField key={field.id} field={field} values={systemValues} />
            )
          )}
        </Box>
      )}
      {detailsData.offer_text && (
        <Stack gap={"sm"}>
          <Title size="xs" header={t("ats:offer_text")} />
          <HtmlViewer content={detailsData.offer_text} />
        </Stack>
      )}
      <PublicLinkSection />
    </Stack>
  );
}
